// Primitivas de maquetación de la documentación.
//
// El contenido se escribe en JSX con estos componentes en vez de en Markdown a propósito: meter
// un renderizador de Markdown obligaba a añadir una dependencia nueva al frontend, y en este
// proyecto instalar paquetes tiene su propia trampa (yarn obligatorio, resolutions fijadas — ver
// CLAUDE.md). Con estas piezas se escribe casi tan cómodo y no se toca el package.json.
import { AlertTriangle, Info, Lightbulb, Check } from "lucide-react";

export const H2 = ({ children, id }) => (
  <h2 id={id} className="scroll-mt-24 text-2xl font-bold text-slate-900 mt-12 mb-4 first:mt-0">{children}</h2>
);

export const H3 = ({ children, id }) => (
  <h3 id={id} className="scroll-mt-24 text-lg font-bold text-slate-900 mt-8 mb-3">{children}</h3>
);

export const P = ({ children }) => (
  <p className="text-[15px] leading-7 text-slate-700 mb-4">{children}</p>
);

export const B = ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>;

export const UL = ({ children }) => (
  <ul className="mb-4 space-y-2 text-[15px] leading-7 text-slate-700">{children}</ul>
);

export const LI = ({ children }) => (
  <li className="flex gap-2.5">
    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
    <span>{children}</span>
  </li>
);

// Pasos numerados — para los flujos de "haz esto, luego esto".
export const Steps = ({ children }) => (
  <ol className="mb-6 space-y-4 counter-reset">{children}</ol>
);

export const Step = ({ n, title, children }) => (
  <li className="flex gap-4">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">{n}</span>
    <div className="min-w-0 flex-1">
      {title && <div className="font-semibold text-slate-900 mb-1">{title}</div>}
      <div className="text-[15px] leading-7 text-slate-700">{children}</div>
    </div>
  </li>
);

const CALLOUT = {
  info: { Icon: Info, box: "border-blue-200 bg-blue-50/70", icon: "text-blue-600" },
  aviso: { Icon: AlertTriangle, box: "border-amber-200 bg-amber-50/70", icon: "text-amber-600" },
  truco: { Icon: Lightbulb, box: "border-emerald-200 bg-emerald-50/70", icon: "text-emerald-600" },
};

export const Callout = ({ tipo = "info", titulo, children }) => {
  const { Icon, box, icon } = CALLOUT[tipo] || CALLOUT.info;
  return (
    <div className={`mb-5 flex gap-3 rounded-xl border px-4 py-3.5 ${box}`}>
      <Icon size={18} className={`mt-0.5 shrink-0 ${icon}`} />
      <div className="min-w-0 text-[15px] leading-7 text-slate-700">
        {titulo && <div className="font-semibold text-slate-900">{titulo}</div>}
        {children}
      </div>
    </div>
  );
};

// Tabla con scroll propio: en móvil una tabla ancha no debe empujar el ancho de la página.
export const Table = ({ head, rows }) => (
  <div className="mb-6 -mx-1 overflow-x-auto">
    <table className="w-full min-w-[420px] text-[15px]">
      <thead>
        <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
          {head.map((h, i) => (
            <th key={i} className={`pb-2 pr-4 font-semibold ${i > 0 ? "text-right" : ""}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100 last:border-0">
            {r.map((c, j) => (
              <td key={j} className={`py-2.5 pr-4 ${j > 0 ? "text-right tabular-nums" : "font-medium text-slate-800"} text-slate-600`}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Lista de "qué sí / qué no" — la sección más útil de una portada de documentación: deja que el
// usuario juzgue si la herramienta le encaja ANTES de gastar créditos aprendiéndolo por su cuenta.
export const SiNo = ({ si, no }) => (
  <div className="mb-6 grid gap-4 sm:grid-cols-2">
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
      <div className="mb-3 flex items-center gap-2 font-bold text-emerald-800"><Check size={18} /> Para esto sirve</div>
      <ul className="space-y-2 text-[15px] leading-6 text-slate-700">
        {si.map((x, i) => <li key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />{x}</li>)}
      </ul>
    </div>
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
      <div className="mb-3 flex items-center gap-2 font-bold text-slate-700"><AlertTriangle size={18} /> Para esto no</div>
      <ul className="space-y-2 text-[15px] leading-6 text-slate-700">
        {no.map((x, i) => <li key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />{x}</li>)}
      </ul>
    </div>
  </div>
);

export const Code = ({ children }) => (
  <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px] text-slate-800">{children}</code>
);
